declare global {
  interface Window {
    YaPay?: YaPaySDK;
  }
}

export interface YaPaySDK {
  mountBadge: (
    element: Element,
    options: {
      type: 'bnpl' | 'cashback' | 'ultimate';
      amount: string;
      size?: 's' | 'm' | 'l';
      variant?: 'simple' | 'detailed' | 'default' | 'compact';
      color?: 'primary' | 'green' | 'grey' | 'transparent';
      theme?: 'light' | 'dark';
      align?: 'left' | 'right';
      merchantId: string;
    }
  ) => void;
}

let sdkPromise: Promise<YaPaySDK> | null = null;

export function loadYandexPaySdk(): Promise<YaPaySDK> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Yandex Pay SDK requires window'));
  }

  if (window.YaPay) return Promise.resolve(window.YaPay);

  if (!sdkPromise) {
    sdkPromise = new Promise<YaPaySDK>((resolve, reject) => {
      const existing = document.querySelector(
        `script[src="${process.env.NEXT_PUBLIC_YANDEX_PAY_SDK_URL || ''}"]`
      ) as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener(
          'load',
          () => {
            if (window.YaPay) resolve(window.YaPay);
          },
          {
            once: true
          }
        );
        existing.addEventListener(
          'error',
          () => reject(new Error('Failed to load Yandex Pay SDK')),
          { once: true }
        );
      } else {
        const script = document.createElement('script');
        script.src = process.env.NEXT_PUBLIC_YANDEX_PAY_SDK_URL || '';
        script.async = true;
        script.onload = () => {
          if (window.YaPay) resolve(window.YaPay);
        };
        script.onerror = () =>
          reject(new Error('Failed to load Yandex Pay SDK'));
        document.body.appendChild(script);
      }
    });
  }

  return sdkPromise;
}

export interface MountBnplBadgeOptions {
  element: Element;
  amount: number | null | undefined;
  size?: 's' | 'm' | 'l';
  variant?: 'simple' | 'detailed';
  color?: 'primary' | 'green' | 'grey' | 'transparent';
  theme?: 'light' | 'dark';
  align?: 'left' | 'right';
}

/** Очередь монтирования: только один mount за раз, чтобы не дублировать бейдж при двойном вызове (Strict Mode) */
let lastMountPromise: Promise<void> = Promise.resolve();

export async function mountBnplBadge({
  element,
  amount,
  size = 'm',
  variant = 'detailed',
  color = 'transparent',
  theme = 'light',
  align = 'left'
}: MountBnplBadgeOptions): Promise<void> {
  if (!amount || amount <= 0) return;

  lastMountPromise = lastMountPromise.then(async () => {
    element.innerHTML = '';
    const sdk = await loadYandexPaySdk();
    sdk.mountBadge(element, {
      type: 'bnpl',
      amount: amount.toFixed(2),
      merchantId: process.env.NEXT_PUBLIC_YANDEX_PAY_MERCHANT_ID || '',
      size,
      variant,
      color,
      theme,
      align
    });
  });

  await lastMountPromise;
}
